'use strict';

/*
  Offer

  Handles the active , expired and previous offers in the Offers Model.

  Parameters:
    presenter [APIPresenter] (required):
      Reference to the APIPresenter in order to communicate with SOA and create data models.

    offerType [string] (required): active, expired or previous
      active: fetches an active offer from the Offers model. An active offer is an offer that is not expired
      expired: fetches an expired offer from the Offers model. An offer that has an expired date.
      previous: fetches a previous offer from the Offers model. When the offers model has more than 1 active offer.
                this offer is an _active_ offer that is next inline with a earlier expiry date. (compares it with current active).
*/

function Offer(presenter, offerType) {
  var _this = this;

  this._presenter = presenter;
  this._offerModel = null;
  this._offerIndex = null;
  this._hasEnhanced = false;
  this._offerType = offerType;
  this._paymentOptions = {};

  this._constructor = function () {
    var offers = this._presenter.getModelProperty('money.offers.offers');

    // Backwards Compatible code, previous offerType param was boolean.
    if (offerType === true) {
      this._offerType = 'expired';
    } else if (offerType === false) {
      this._offerType = 'active';
    }

    if (offers && offers.length) {
      // Active
      if (this._offerType === 'active') {
        offers.forEach(this._getActiveOffer.bind(this));
      } else if (this._offerType === 'previous') {
        var enhancedDate = this._getEnhancedIndex(offers);

        if (enhancedDate > -1) {
          offers.forEach(this._getPreviousOffer.bind(this, enhancedDate));
        }
      } else if (this._offerType === 'expired') {
        offers.forEach(this._getExpiredOffer.bind(this));
      }
    }
  };

  this._getExpiredOffer = function (offer, index, arr) {
    // Use expiry date in current offer as bench mark for comparison
    var date = offer[0].expires_at;

    if (_this.isExpired(date)) {
      if (_this._offerIndex === null) {
        _this._offerIndex = index;
        _this._offerModel = arr[_this._offerIndex][0];
      }

      // Expire Logic
      if (moment(date).isSameOrBefore(arr[_this._offerIndex][0].expires_at)) {
        _this._offerIndex = index;
        _this._offerModel = arr[_this._offerIndex][0];
      }
    }
  };

  this._getActiveOffer = function (offer, index, arr) {
    // Use expiry date in current offer as bench mark for comparison
    var date = offer[0].expires_at;
    var name = offer[0].offer_type;

    if (!_this.isExpired(date)) {
      if (_this._offerIndex === null) {
        _this._offerIndex = index;
        _this._offerModel = arr[_this._offerIndex][0];
      }

      // Enhanced
      if (name === 'enhanced') {
        _this._hasEnhanced = true;

        _this._offerIndex = index;
        _this._offerModel = arr[_this._offerIndex][0];
      } else if (_this._hasEnhanced === false && moment(date).isSameOrAfter(arr[_this._offerIndex][0].expires_at)) {
        _this._offerIndex = index;
        _this._offerModel = arr[_this._offerIndex][0];
      }
    }
  };

  this._getPreviousOffer = function (enhancedIndex, offer, index, arr) {
    // Use expiry date in current offer as bench mark for comparison
    var date = offer[0].expires_at;
    var name = offer[0].offer_type;
    var enhancedDate = arr[enhancedIndex][0].expires_at;

    if (!_this.isExpired(date)) {
      // Previous, Enhanced must exist
      if (_this._hasEnhanced === false && name !== 'enhanced' && moment(enhancedDate).isSameOrAfter(date)) {
        _this._hasEnhanced = true;

        _this._offerIndex = index;
        _this._offerModel = arr[_this._offerIndex][0];
      }

      if (_this._hasEnhanced === true && moment(date).isSameOrAfter(arr[_this._offerIndex][0].expires_at)) {
        _this._offerIndex = index;
        _this._offerModel = arr[_this._offerIndex][0];
      }
    }
  };

  this._getEnhancedIndex = function (offerArray) {
    var index = offerArray.findIndex(function (offer, index) {
      // Use expiry date in current offer as bench mark for comparison
      var name = offer[0].offer_type;

      return name === 'enhanced';
    });

    return index;
  };

  this.isExpired = function (date) {
    var tzOffset = _this._presenter.getModelProperty('account.timezone');
    tzOffset = tzOffset ? tzOffset.substr(tzOffset.lastIndexOf(' ')).trim() : '';

    return moment(date).utcOffset(tzOffset).isBefore(moment().utcOffset(tzOffset));
  };

  // To retrieve raw data of an offer OR verify if an active/expired offer exists
  this.getOfferData = function () {
    return this._offerModel;
  };

  this.getOfferName = function () {
    return this._offerModel ? this._offerModel.name : null;
  };

  this.getOfferType = function () {
    return this._offerModel ? this._offerModel.offer_type : null;
  };

  this.getOfferAmount = function () {
    return this._offerModel ? this._offerModel.amount : null;
  };

  this.getOfferApr = function () {
    return this._offerModel ? this._offerModel.apr : null;
  };

  //  Returns offer payment array; Note this is NOT the full payment option array.
  this.getOfferPayments = function () {
    return this._offerModel ? this._offerModel.payments[0] : null;
  };

  //  Returns offer payment array; Note this is NOT the full payment option array.
  this.getOfferTerm = function () {
    return this._offerModel ? this._offerModel.payments[0].terms : null;
  };

  this.getOfferTermInPreferredUnits = function () {
    if (this._offerModel) {
      var offerPayments = this._offerModel.payments[0];
      if (offerPayments.display_terms_as === 'month') {
        return offerPayments.term_months + ' month';
      }
      if (offerPayments.display_terms_as === 'year') {
        return +parseFloat(offerPayments.term_months / 12).toFixed(2) + ' year';
      }
    }
  };

  //  Returns offer status from is_paused property
  this.isOfferPaused = function () {
    return this._offerModel ? !!this._offerModel.is_paused : null;
  };

  this.getOfferMinAmount = function () {
    return this._offerModel ? this._offerModel.minimum_amount : null;
  };

  this.getLoanReferenceId = function () {
    return _this._offerModel ? _this._offerModel.levelup_loan_id ? _this._offerModel.levelup_loan_id : null : null;
  };

  this.getOfferLenderName = function () {
    var lenders = this._presenter.getModelProperty('money.lenders.lenders');
    if (this._offerModel && lenders) {
      for (var i = 0; i < lenders.length; i++) {
        if (this._offerModel.lender_id === lenders[i].id) return lenders[i].name;
      }
    }
    return null;
  };

  this.getExpireDate = function () {
    var offers = this._presenter.getModelProperty('money.offers.offers');
    if (offers && offers.length > 0 && offers[this._offerIndex]) {
      return offers[this._offerIndex][0].expires_at;
    }
    return null;
  };

  this.getDaysBeforeExpiration = function () {
    var tzOffset = this._presenter.getModelProperty('account.timezone');
    tzOffset = tzOffset ? tzOffset.substr(tzOffset.lastIndexOf(' ')).trim() : '';
    var expireDate = this.getExpireDate();
    // if expired already, return null
    if (!expireDate || moment(expireDate).utcOffset(tzOffset).isBefore(moment().utcOffset(tzOffset))) {
      return null;
    }
    // (expire date + timezoneoffset) - (current date + timezoneoffset)
    return moment(expireDate).utcOffset(tzOffset).diff(moment().utcOffset(tzOffset), 'days');
  };

  this.getPaymentOption = function (amount, callback) {
    var paymentProperty = 'money.offers.offers[' + this._offerIndex + '][0].payment_options';

    // Check if model is there
    if (!this._presenter.getModelProperty(paymentProperty)) {
      this.getPaymentOptions({
        success: function (callback) {
          callback(this._paymentOptions[amount]);
        }.bind(this, callback)
      });
    } else {
      // Payment options might not exist if there are no instances of the class.
      if ($.isEmptyObject(this._paymentOptions)) {
        this._parsePaymentOptions(this._presenter.getModelProperty(paymentProperty));
      }
      callback(this._paymentOptions[amount]);
    }
  };

  this.getPaymentOptions = function (callbacks) {
    if (this._presenter.hasAction('money.offers.offers[' + this._offerIndex + '][0].get_payment_options')) {
      this._presenter.performAction('money.offers.offers[' + this._offerIndex + '][0].get_payment_options', null, this._onGetPaymentOptionsCallback.bind(this, callbacks));
    } else if (callbacks.failure) {
      callbacks.failure({
        errors: [{
          code: 404,
          key: 'no_action_error'
        }]
      }, 404);
    }
  };

  this._onGetPaymentOptionsCallback = function (callbacks, data, response) {
    if (response === 200) {
      this._presenter.createModel('money.offers.offers[' + this._offerIndex + '][0].payment_options', {
        data: data
      }, function (callbacks, data, response) {
        this._parsePaymentOptions(data);
        if (callbacks.success) {
          callbacks.success(data, response);
        }
      }.bind(this, callbacks, data, response));
    } else if (callbacks.failure) {
      callbacks.failure(data, response);
    }
  };

  // Puts array in to an obj for performance
  this._parsePaymentOptions = function (data) {
    var _this2 = this;

    // data is an array
    data.payment_options.forEach(function (element, index) {
      _this2._paymentOptions[element.amount] = element;
    });
  };

  this._constructor();
}